import { createSlice } from '@reduxjs/toolkit';
import axios from 'utils/axios';

const initialState = {
  /**
   * The only value the server ever queries. It used to be a wizard step offering
   * "Transaction" or "Event"; Event produced a webhook nothing would ever trigger, so the
   * step is gone and this is fixed.
   */
  dataSource: 'transaction',
  integration: '',
  isLoading: false,
  error: false,
  list: [],
  logs: [],
  /** Event types and signature scheme, served by the API so a new event type does not need a panel release. */
  catalog: null,
  deliveries: { rows: [], total: 0, isLoading: false },
  /**
   * Held in memory only, and only until the reveal dialog is dismissed. The API returns a
   * signing secret exactly once — there is no endpoint that can hand it back, so nothing
   * here may persist it anywhere the user cannot see.
   */
  revealedSecret: null,
};

const slice = createSlice({
  name: 'webhook',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getWebhooksSuccess(state, action) {
      state.isLoading = false;
      state.list = action.payload;
    },

    getWebhookLogsSuccess(state, action) {
      state.isLoading = false;
      state.logs = action.payload;
    },

    setIntegration(state, action) {
      state.integration = action.payload;
    },

    getCatalogSuccess(state, action) {
      state.catalog = action.payload;
    },

    startDeliveriesLoading(state) {
      state.deliveries.isLoading = true;
    },

    getDeliveriesSuccess(state, action) {
      state.deliveries = {
        rows: action.payload.deliveries ?? [],
        total: action.payload.total ?? 0,
        isLoading: false,
      };
    },

    revealSecret(state, action) {
      state.revealedSecret = action.payload;
    },

    clearRevealedSecret(state) {
      state.revealedSecret = null;
    },
  },
});

export default slice.reducer;

export const { setIntegration, clearRevealedSecret } = slice.actions;

export function getWebhooks(appId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/webhooks/${appId}`);
      dispatch(slice.actions.getWebhooksSuccess(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getWebhookLogs(dataSourceId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/webhooks/logs/${dataSourceId}`);
      dispatch(slice.actions.getWebhookLogsSuccess(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/* -------------------------------------------------------------------------- */
/* Customer webhooks                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The event catalog comes from the API rather than a constant in this repo, so adding an
 * event type server-side makes it selectable here without a panel release.
 */
export function getWebhookCatalog() {
  return async (dispatch) => {
    try {
      const response = await axios.get('/webhooks/catalog');
      dispatch(slice.actions.getCatalogSuccess(response.data ?? response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Creating an endpoint is the only moment the signing secret exists in a response. It goes
 * straight into `revealedSecret` for the dialog to show, and the caller is responsible for
 * clearing it once dismissed — it cannot be fetched again.
 */
export function createCustomerWebhook(appId, { url, events }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/webhooks/${appId}/endpoints`, { url, events });
      const data = response.data ?? response;
      dispatch(slice.actions.revealSecret({ secret: data.secret, context: 'created' }));
      await dispatch(getWebhooks(appId));
      return data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateCustomerWebhook(appId, webhookId, changes) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.put(`/webhooks/${appId}/endpoints/${webhookId}`, changes);
      await dispatch(getWebhooks(appId));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteCustomerWebhook(appId, webhookId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete(`/webhooks/${appId}/${webhookId}`);
      await dispatch(getWebhooks(appId));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

/**
 * Rotation returns the new secret and keeps the previous one alive for an overlap window,
 * so the consumer can deploy at their own pace. The old secret is only dropped when
 * `retireWebhookSecret` is called.
 */
export function rotateWebhookSecret(appId, webhookId) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`/webhooks/${appId}/endpoints/${webhookId}/rotate-secret`);
      const data = response.data ?? response;
      dispatch(
        slice.actions.revealSecret({
          secret: data.secret,
          context: 'rotated',
          previousExpiresAt: data.previousExpiresAt,
        }),
      );
      await dispatch(getWebhooks(appId));
      return data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function retireWebhookSecret(appId, webhookId) {
  return async (dispatch) => {
    try {
      await axios.post(`/webhooks/${appId}/endpoints/${webhookId}/retire-secret`);
      await dispatch(getWebhooks(appId));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function getWebhookDeliveries(appId, webhookId, { status, limit, offset } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startDeliveriesLoading());
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));

      const query = params.toString();
      const response = await axios.get(
        `/webhooks/${appId}/endpoints/${webhookId}/deliveries${query ? `?${query}` : ''}`,
      );
      dispatch(slice.actions.getDeliveriesSuccess(response.data ?? response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Returns the result rather than storing it: it belongs to the panel that asked, not to
 * shared state, and it must not linger after the user navigates away.
 *
 * The endpoint answers 200 even when the customer's endpoint rejected the delivery — the
 * rejection is the answer, not an error — so the diagnostic is in the response body.
 */
export function sendWebhookTestEvent(appId, webhookId, eventType) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`/webhooks/${appId}/endpoints/${webhookId}/test`, { eventType });
      return response.data ?? response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function replayWebhookDelivery(appId, webhookId, logId) {
  return async (dispatch) => {
    try {
      await axios.post(`/webhooks/${appId}/endpoints/${webhookId}/deliveries/${logId}/replay`);
      // The replay lands as a new row rather than mutating the old one, so refetch instead
      // of patching state — the list is the history, and it just grew.
      await dispatch(getWebhookDeliveries(appId, webhookId));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}
